import { useSyncExternalStore } from 'react';

/**
 * Theme switching for the design system (ADR 0043).
 *
 * The active theme is the `data-theme="light" | "dark"` attribute on `<html>`; `tokens.dark.css`
 * keys its single dark block off that attribute and sets `color-scheme` per theme so native controls
 * follow. This module owns the attribute at runtime:
 *
 * - `useTheme()` — for components: the user's preference, what is on screen, and a setter.
 * - `startThemeSync()` — once, at app start: keeps the attribute following OS changes and other
 *   tabs. The Storybook decorator calls it for the preview iframe.
 *
 * First paint is handled before React by the inline bootstrap in `public/index.html`, which
 * applies the same storage key and resolution rule. Change the two together.
 */

/** What the user asked for. `system` follows `prefers-color-scheme`. */
export type ThemePreference = 'system' | 'light' | 'dark';

/** What is actually on screen. */
export type ResolvedTheme = 'light' | 'dark';

/** localStorage key. Mirrored by the bootstrap script in `public/index.html`. */
export const THEME_STORAGE_KEY = 'envd.theme';

/** Attribute on `<html>` that `tokens.dark.css` selects the dark block with. */
export const THEME_ATTRIBUTE = 'data-theme';

const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';
const THEME_PREFERENCES: readonly string[] = ['system', 'light', 'dark'];

export const isThemePreference = (value: unknown): value is ThemePreference =>
    typeof value === 'string' && THEME_PREFERENCES.includes(value);

const readStoredPreference = (): ThemePreference => {
    try {
        const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
        return isThemePreference(stored) ? stored : 'system';
    } catch {
        return 'system';
    }
};

const writeStoredPreference = (preference: ThemePreference): void => {
    try {
        if (preference === 'system') {
            window.localStorage.removeItem(THEME_STORAGE_KEY);
        } else {
            window.localStorage.setItem(THEME_STORAGE_KEY, preference);
        }
    } catch {
        // Storage blocked (private mode, disabled cookies). The choice still applies for this page.
    }
};

const systemPrefersDark = (): boolean =>
    typeof window.matchMedia === 'function' && window.matchMedia(DARK_SCHEME_QUERY).matches;

/** Turns a preference into the theme to draw, consulting the OS for `system`. */
export const resolveTheme = (preference: ThemePreference): ResolvedTheme =>
    preference === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : preference;

/**
 * Writes the resolved theme to `<html data-theme>` and points the browser-chrome `theme-color`
 * meta at the page background for that theme. The meta is read from the computed `--bg-default`
 * so it stays token-driven; when the token cannot be computed (no stylesheet, jsdom) the meta is
 * left alone.
 */
export const applyTheme = (resolved: ResolvedTheme): void => {
    const root = document.documentElement;
    root.setAttribute(THEME_ATTRIBUTE, resolved);

    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (meta) {
        const background = getComputedStyle(root).getPropertyValue('--bg-default').trim();
        if (background) {
            meta.content = background;
        }
    }
};

/* ── Store ──────────────────────────────────────────────────────────────────────
   A tiny external store rather than React context: the theme lives on the document, not in the
   tree, so there is nothing for a provider to scope. `useSyncExternalStore` gives components a
   tear-free read; the environment listeners (OS scheme, other tabs) attach while anything is
   subscribed and detach when the last subscriber leaves.

   localStorage is the durable record. The in-memory copy exists so a choice still applies when
   storage is blocked, and it is re-read from storage whenever nothing is subscribed, so the store
   never outlives its subscribers with stale state.
*/

let preference: ThemePreference = 'system';
const listeners = new Set<() => void>();
let detachEnvironment: (() => void) | undefined;

const emit = (): void => listeners.forEach((listener) => listener());

const applyCurrent = (): void => applyTheme(resolveTheme(preference));

export const getThemePreference = (): ThemePreference => {
    if (listeners.size === 0) {
        preference = readStoredPreference();
    }
    return preference;
};

export const getResolvedTheme = (): ResolvedTheme => resolveTheme(getThemePreference());

/** Sets, persists and applies the preference. `system` clears the stored value. */
export const setThemePreference = (next: ThemePreference): void => {
    preference = next;
    writeStoredPreference(next);
    applyCurrent();
    emit();
};

const attachEnvironment = (): (() => void) => {
    const onSchemeChange = (): void => {
        // Only observable in `system` mode, but re-applying an explicit choice is harmless.
        applyCurrent();
        emit();
    };
    const onStorage = (event: StorageEvent): void => {
        if (event.key === THEME_STORAGE_KEY || event.key === null) {
            preference = readStoredPreference();
            applyCurrent();
            emit();
        }
    };

    const mediaQueryList = typeof window.matchMedia === 'function' ? window.matchMedia(DARK_SCHEME_QUERY) : undefined;
    mediaQueryList?.addEventListener('change', onSchemeChange);
    window.addEventListener('storage', onStorage);

    return () => {
        mediaQueryList?.removeEventListener('change', onSchemeChange);
        window.removeEventListener('storage', onStorage);
    };
};

/** Subscribes to preference and resolved-theme changes. Returns the unsubscribe. */
export const subscribeToTheme = (listener: () => void): (() => void) => {
    if (listeners.size === 0) {
        preference = readStoredPreference();
        detachEnvironment = attachEnvironment();
    }
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
            detachEnvironment?.();
            detachEnvironment = undefined;
        }
    };
};

/**
 * Applies the stored (or OS) theme now and keeps `<html data-theme>` in step with OS changes and
 * other tabs until the returned stop function is called. Call once at app start.
 */
export const startThemeSync = (): (() => void) => {
    const stop = subscribeToTheme(() => undefined);
    applyCurrent();
    return stop;
};

const getServerPreference = (): ThemePreference => 'system';
const getServerResolved = (): ResolvedTheme => 'light';

export interface UseThemeResult {
    /** The user's choice. `system` means "follow the OS". */
    theme: ThemePreference;
    /** What is on screen right now. */
    resolvedTheme: ResolvedTheme;
    /** Sets, persists and applies a new choice. */
    setTheme: (next: ThemePreference) => void;
}

/**
 * @example
 * const { theme, resolvedTheme, setTheme } = useTheme();
 * <Switch checked={resolvedTheme === 'dark'} onChange={(on) => setTheme(on ? 'dark' : 'light')} />
 * <Button onClick={() => setTheme('system')}>Use system setting</Button>
 */
export const useTheme = (): UseThemeResult => {
    const theme = useSyncExternalStore(subscribeToTheme, getThemePreference, getServerPreference);
    const resolvedTheme = useSyncExternalStore(subscribeToTheme, getResolvedTheme, getServerResolved);
    return { theme, resolvedTheme, setTheme: setThemePreference };
};
